import { useMemo } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
} from "@tamagui/lucide-icons";
import type { ToastProps } from "@tamagui/toast";
import { Toast, useToastState } from "@tamagui/toast";
import { XStack } from "tamagui";

export const AppToast = () => {
  const currentToast = useToastState();

  const variant = currentToast?.variant ?? "default";
  const styles = useMemo<ToastProps>(() => {
    switch (variant) {
      case "success":
        return {
          color: "$score-8",
          borderColor: "$score-8",
          backgroundColor: "$lightgreen-100",
        };
      case "error":
        return {
          color: "$error-500",
          borderColor: "$error-500",
          backgroundColor: "$error-500-o005",
        };
      case "warning":
        return {
          color: "$warning-500",
          borderColor: "$warning-500",
          backgroundColor: "$warm-snow-500",
        };
      default:
        return {
          borderColor: "$gray-500",
          backgroundColor: "$gray-50",
        };
    }
  }, [variant]);

  const ToastIcon = useMemo(() => {
    switch (variant) {
      case "success":
        return CheckCircle2;
      case "error":
        return XCircle;
      case "warning":
        return AlertTriangle;
      default:
        return Info;
    }
  }, [variant]);

  if (!currentToast || currentToast.isHandledNatively) return null;

  return (
    <Toast
      key={currentToast.id}
      duration={currentToast.duration}
      borderWidth={"$0.2"}
      {...styles}
    >
      <XStack alignItems="center" gap={"$1"}>
        <ToastIcon
          color={styles.borderColor as string}
          width={20}
          height={20}
        />
        <Toast.Title color={styles.borderColor} fontWeight={"$5"}>
          {currentToast.title}
        </Toast.Title>
      </XStack>
      {currentToast.message ? (
        <Toast.Description>{currentToast.message}</Toast.Description>
      ) : null}
      <Toast.Close />
    </Toast>
  );
};
